import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Truck,
  Package,
  Globe,
  Calendar,
  Loader2,
  Link,
  AlertCircle,
  X,
  Save,
  MapPin,
  Clock,
  CheckCircle,
  Sparkles,
  Printer,
  Settings,
  Zap
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Order } from '../types';
import { EasyPostLabelModal } from './EasyPostLabelModal';
import { formatCurrency, formatDateTime, formatDate } from '../lib/utils';
import { Carrier, generateCarrierTrackingUrl } from '../services/carrierService';

interface AddTrackingModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onTrackingUpdate?: (orderId: string, trackingData: TrackingData) => Promise<void>;
  onEasyPostLabelGeneration?: (orderId: string) => Promise<void>;
  carriers: Carrier[];
  loading: boolean;
}

interface TrackingData {
  carrier: string;
  carrierName: string;
  trackingNumber: string;
  url?: string;
  status: string;
  estimatedDelivery?: Date;
}

const statusOptions = [
  { value: 'label_created', label: 'Label Created', description: 'Shipping label has been created' },
  { value: 'picked_up', label: 'Picked Up', description: 'Package picked up by carrier' },
  { value: 'in_transit', label: 'In Transit', description: 'Package is on its way' },
  { value: 'out_for_delivery', label: 'Out for Delivery', description: 'Package is out for delivery' },
  { value: 'delivered', label: 'Delivered', description: 'Package has been delivered' },
  { value: 'exception', label: 'Exception', description: 'Delivery exception occurred' }
];

export function AddTrackingModal({ order, isOpen, onClose, onTrackingUpdate, onEasyPostLabelGeneration, carriers, loading }: AddTrackingModalProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState('manual');
  
  // Manual tracking state
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [trackingStatus, setTrackingStatus] = useState('label_created');
  const [estimatedDelivery, setEstimatedDelivery] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // EasyPost state
  const [showEasyPostModal, setShowEasyPostModal] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen && order) {
      if (order.tracking) {
        setCarrier(order.tracking.carrier || '');
        setTrackingNumber(order.tracking.trackingNumber || '');
        setTrackingUrl(order.tracking.url || '');
        setTrackingStatus(order.tracking.status || 'label_created');
        setEstimatedDelivery(order.tracking.estimatedDelivery 
          ? (() => {
              const date = new Date(order.tracking.estimatedDelivery);
              return !isNaN(date.getTime()) ? date.toISOString().split('T')[0] : '';
            })()
          : ''
        );
      } else {
        setCarrier('');
        setTrackingNumber('');
        setTrackingUrl('');
        setTrackingStatus('label_created');
        setEstimatedDelivery('');
      }
    } else {
      setCarrier('');
      setTrackingNumber('');
      setTrackingUrl('');
      setTrackingStatus('label_created');
      setEstimatedDelivery('');
    }
  }, [isOpen, order]);

  if (!isOpen || !order) return null;

  const selectedCarrier = carriers.find(c => c.code === carrier);
  const selectedStatus = statusOptions.find(s => s.value === trackingStatus);
  const isFormValid = carrier && trackingNumber.trim();

  // Helper function to format status display
  const formatTrackingStatus = (status: string) => {
    const statusMap: { [key: string]: { label: string; color: string; bgColor: string } } = {
      'label_created': { label: 'Label Created', color: 'text-jamaica-green', bgColor: 'bg-jamaica-green/10' },
      'picked_up': { label: 'Picked Up', color: 'text-jamaica-yellow', bgColor: 'bg-jamaica-yellow/10' },
      'in_transit': { label: 'In Transit', color: 'text-blue-700', bgColor: 'bg-blue-100' },
      'out_for_delivery': { label: 'Out for Delivery', color: 'text-orange-700', bgColor: 'bg-orange-100' },
      'delivered': { label: 'Delivered', color: 'text-green-700', bgColor: 'bg-green-100' },
      'exception': { label: 'Exception', color: 'text-red-700', bgColor: 'bg-red-100' }
    };
    
    return statusMap[status] || { label: status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), color: 'text-jamaica-black', bgColor: 'bg-gray-100' };
  };

  // Helper function to get carrier display name
  const getCarrierDisplayName = (carrierCode: string) => {
    const carrier = carriers.find(c => c.code === carrierCode);
    return carrier ? carrier.name : carrierCode.toUpperCase();
  };

  const handleCarrierChange = (value: string) => {
    setCarrier(value);
    // Auto-generate URL if tracking number exists
    if (trackingNumber && value) {
      const carrierObj = carriers.find(c => c.code === value);
      if (carrierObj) {
        const autoUrl = generateCarrierTrackingUrl(carrierObj, trackingNumber);
        if (autoUrl) {
          setTrackingUrl(autoUrl);
        }
      }
    }
  };

  const handleTrackingNumberChange = (value: string) => {
    setTrackingNumber(value);
    // Auto-generate URL if carrier is selected
    if (carrier && value) {
      const carrierObj = carriers.find(c => c.code === carrier);
      if (carrierObj) {
        const autoUrl = generateCarrierTrackingUrl(carrierObj, value);
        if (autoUrl) {
          setTrackingUrl(autoUrl);
        }
      }
    } else if (!value) {
      // Clear the URL if tracking number is empty
      setTrackingUrl('');
    }
  };

  const handleEasyPostGeneration = () => {
    setShowEasyPostModal(true);
  };

  const handleEasyPostSuccess = () => {
    setShowEasyPostModal(false);
    onClose();
    if (onEasyPostLabelGeneration) {
      onEasyPostLabelGeneration(order?.id || '');
    }
  };

  const handleTrackingUpdate = async () => {
    console.log('handleTrackingUpdate called', {
      carrier,
      trackingNumber,
      trackingUrl,
      trackingStatus,
      estimatedDelivery
    });

    if (!isFormValid) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!onTrackingUpdate) {
      console.error('onTrackingUpdate function not provided');
      toast.error('Tracking update function not available');
      return;
    }

    try {
      setIsUpdating(true);
      
      // Get the selected carrier object to include the name
      const carrierObj = carriers.find(c => c.code === carrier);
      
      const trackingData: TrackingData = {
        carrier: carrier, // Store the code for backwards compatibility
        carrierName: carrierObj?.name || carrier.toUpperCase(), // Store the formatted name
        trackingNumber: trackingNumber.trim(),
        url: trackingUrl.trim() || undefined,
        status: trackingStatus,
        estimatedDelivery: estimatedDelivery ? new Date(estimatedDelivery) : undefined
      };

      console.log('Calling onTrackingUpdate with:', {
        orderId: order.id,
        trackingData
      });
      
      await onTrackingUpdate(order.id, trackingData);
      
      toast.success(
        'Tracking information updated successfully! ' +
        'The system will automatically monitor this tracking number for delivery updates.',
        { duration: 5000 }
      );
      onClose();
    } catch (error) {
      console.error('Failed to update tracking information:', error);
      toast.error('Failed to update tracking information. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const getOrderNumber = () => {
    if (!order) return '';
    return order.orderNumber || order.id;
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-orange-500 to-amber-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <Truck className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    {order.tracking ? 'Update' : 'Add'} Tracking
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm"
                  >
                    Order #{getOrderNumber()} • {formatCurrency(order.total || 0)}
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Form with Tabs */}
          <div className="p-8 overflow-y-auto flex-1 bg-gradient-to-b from-gray-50/50 to-white">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="manual" className="flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Manual Tracking
                </TabsTrigger>
                <TabsTrigger value="easypost" className="flex items-center gap-2">
                  <Printer className="h-4 w-4" />
                  Generate Label
                </TabsTrigger>
              </TabsList>

              {/* Manual Tracking Tab */}
              <TabsContent value="manual" className="space-y-8 mt-0">
                {/* Auto-Tracking Info Banner */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <Zap className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <h4 className="font-medium text-green-800">
                        Automatic Delivery Tracking
                      </h4>
                      <p className="text-sm text-green-700">
                        When you add tracking information, the system automatically monitors the tracking number 
                        and will update the order status to "delivered" when the package arrives at its destination.
                      </p>
                    </div>
                  </div>
                </motion.div>

            {/* Carrier Selection */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-3"
            >
              <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Truck className="h-4 w-4 text-orange-500" />
                Shipping Carrier *
              </Label>
              <Select value={carrier} onValueChange={handleCarrierChange} disabled={loading}>
                <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <SelectValue placeholder="Select shipping carrier" />
                </SelectTrigger>
                <SelectContent>
                  {carriers.map((carrierOption, index) => (
                    <SelectItem key={carrierOption.code || `carrier-${index}`} value={carrierOption.code}>
                      <div className="flex items-center gap-2">
                        <span>{carrierOption.name}</span>
                        {carrierOption.trackingUrlTemplate && (
                          <Globe className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </motion.div>

            {/* Tracking Number */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-3"
            >
              <Label htmlFor="trackingNumber" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Package className="h-4 w-4 text-orange-500" />
                Tracking Number *
              </Label>
              <Input
                id="trackingNumber"
                value={trackingNumber}
                onChange={(e) => handleTrackingNumberChange(e.target.value)}
                placeholder="Enter tracking number (e.g., 1Z999AA1234567890)"
                className="h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
              />
            </motion.div>

            {/* Tracking URL */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="space-y-3"
            >
              <Label htmlFor="trackingUrl" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Link className="h-4 w-4 text-orange-500" />
                Tracking URL
                <span className="text-xs font-normal text-gray-500">(auto-generated)</span>
              </Label>
              <Input
                id="trackingUrl"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                placeholder="Tracking URL will be auto-generated"
                className="h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
              />
            </motion.div>

            {/* Tracking Status */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="space-y-3"
            >
              <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-orange-500" />
                Current Status
              </Label>
              <Select value={trackingStatus} onValueChange={setTrackingStatus}>
                <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 shadow-sm hover:shadow-md transition-all duration-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{option.label}</span>
                        <span className="text-xs text-gray-500">{option.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </motion.div>

            {/* Estimated Delivery */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="space-y-3"
            >
              <Label htmlFor="estimatedDelivery" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-orange-500" />
                Estimated Delivery
                <span className="text-xs font-normal text-gray-500">(optional)</span>
              </Label>
              <Input
                id="estimatedDelivery"
                type="date"
                value={estimatedDelivery}
                onChange={(e) => setEstimatedDelivery(e.target.value)}
                className="h-12 rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
              />
            </motion.div>

            {/* Live Preview */}
            <AnimatePresence>
              {carrier && trackingNumber && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: 0.8 }}
                  className="space-y-3"
                >
                  <Label className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-orange-500" />
                    Tracking Preview
                  </Label>
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {getCarrierDisplayName(carrier)}
                          </h3>
                          <p className="text-sm text-gray-600 font-mono">{trackingNumber}</p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${formatTrackingStatus(trackingStatus).bgColor} ${formatTrackingStatus(trackingStatus).color}`}>
                          {formatTrackingStatus(trackingStatus).label}
                        </div>
                      </div>
                      {estimatedDelivery && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>Expected delivery: {new Date(estimatedDelivery).toLocaleDateString()}</span>
                        </div>
                      )}
                      {trackingUrl && (
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                          <Link className="w-4 h-4" />
                          <span className="truncate">{trackingUrl}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
              </TabsContent>

              {/* EasyPost Label Generation Tab */}
              <TabsContent value="easypost" className="space-y-8 mt-0">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-center space-y-6"
                >
                  <div className="bg-gradient-to-br from-jamaica-green/10 to-blue-50 rounded-xl p-8 border border-jamaica-green/20">
                    <div className="flex flex-col items-center gap-4">
                      <div className="h-16 w-16 bg-jamaica-green/20 rounded-full flex items-center justify-center">
                        <Zap className="h-8 w-8 text-jamaica-green" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          Generate Shipping Label with EasyPost
                        </h3>
                        <p className="text-gray-600 max-w-md">
                          Automatically generate a professional shipping label with customs declarations and enable 
                          automatic delivery tracking that will update the order status when delivered.
                        </p>
                      </div>
                      
                      {order?.easyPostLabel ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md">
                          <div className="flex items-center gap-2 text-green-800">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-medium">Label Already Generated</span>
                          </div>
                          <p className="text-sm text-green-700 mt-1">
                            Carrier: {order.easyPostLabel.shipment.carrier} • Service: {order.easyPostLabel.shipment.service}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <p className="text-sm text-gray-500 max-w-md">
                            This will use your configured ship-from address, package dimensions, and customs data to generate a ready-to-print shipping label.
                          </p>
                          <Button
                            onClick={handleEasyPostGeneration}
                            className="bg-jamaica-green hover:bg-jamaica-green/90 text-white px-8 h-12 rounded-xl font-semibold shadow-lg shadow-jamaica-green/25"
                          >
                            <Settings className="w-5 h-5 mr-2" />
                            Configure & Generate Label
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Requirements</p>
                        <ul className="list-disc list-inside space-y-1 text-blue-700">
                          <li>Ship-from address must be configured</li>
                          <li>Package dimensions must be set</li>
                          <li>Products must have customs data (manufacturer, HS code, origin country)</li>
                          <li>Valid destination address</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  variant="outline" 
                  onClick={onClose}
                  disabled={isUpdating}
                  className="px-6 h-11 rounded-xl font-medium"
                >
                  Cancel
                </Button>
              </motion.div>
              
              {activeTab === 'manual' && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    onClick={handleTrackingUpdate}
                    disabled={isUpdating || !isFormValid}
                    className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-orange-500/25"
                  >
                    {isUpdating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {order.tracking ? 'Updating...' : 'Adding...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {order.tracking ? 'Update Tracking' : 'Add Tracking'}
                      </>
                    )}
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* EasyPost Label Generation Modal */}
      <EasyPostLabelModal
        order={order}
        isOpen={showEasyPostModal}
        onClose={() => setShowEasyPostModal(false)}
        onSuccess={handleEasyPostSuccess}
      />
    </AnimatePresence>
  );
} 